import { Fragment, useState, useEffect } from "react";
import {
  useOutletContext,
  useLocation,
  useNavigate,
  Link,
  Outlet,
} from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import logo from "../../images/logo.svg";
import { ReactComponent as homeIcon } from "../../images/home.svg";
import { ReactComponent as companyIcon } from "../../images/company.svg";
import { ReactComponent as uploadIcon } from "../../images/upload.svg";
import { ReactComponent as settingsIcon } from "../../images/settings.svg";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const SuperAdminLayout = () => {
  const { token, notify, userDetails, getUserDetails, frontendurl, logOut } =
    useOutletContext();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { t } = useTranslation();

  // get current location
  const { pathname } = useLocation();

  const navigation = [
    {
      name: "Dashboard",
      link: `dashboard`,
      icon: homeIcon,
      current: pathname === "/super-admin/dashboard",
    },
    {
      name: "Onboard Client",
      link: `onboard`,
      icon: uploadIcon,
      current: pathname === "/super-admin/onboard",
    },
    {
      name: "Manage Companies",
      link: `companies`,
      icon: companyIcon,
      current: pathname === "/super-admin/companies",
    },
    {
      name: "Content Management",
      link: `content`,
      icon: settingsIcon,
      current: pathname === "/super-admin/content",
    },
    {
      name: "Settings",
      link: `settings`,
      icon: settingsIcon,
      current: pathname === "/super-admin/settings",
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();

  // redirect to base route if user is in "super-admin" without subroute
  useEffect(() => {
    if (location.pathname === "/super-admin") {
      navigate("/super-admin/dashboard");
    }
  }, [navigate, location.pathname]);

  return (
    <>
      <div className="bg-[#dedff1]">
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-primary"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto p-6 pb-2 bg-white sidebar-mobile">
                    <img
                      width="134"
                      height="23"
                      className="max-w-full block mx-auto mb-10"
                      src={logo}
                      alt="VeeMind"
                    />
                    <nav className="flex flex-1 flex-col">
                      <ul className="">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.link}
                              className={classNames(
                                item.current ? "current" : "",
                                "flex items-center"
                              )}
                            >
                              <item.icon aria-hidden="true" className="mr-3" />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                        <li>
                          <button
                            onClick={logOut}
                            className="button border-primary text-primary w-full mt-8"
                          >
                            {t("menu.logout")}
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col p-8">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 sidebar max-h-full ">
            <img
              width="134"
              height="23"
              className="max-w-full block mx-auto mb-14"
              src={logo}
              alt="VeeMind"
            />
            <div className="overflow-y-auto p-1">
              <nav className="flex flex-1 flex-col">
                <ul className="">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.link}
                        className={classNames(
                          item.current ? "current" : "",
                          "flex items-center"
                        )}
                      >
                        <item.icon aria-hidden="true" className="mr-3" />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button
                      onClick={logOut}
                      className="button border-primary text-primary w-full mt-8"
                    >
                      {t("menu.logout")}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <div className="sticky top-0 z-40 flex items-center gap-x-6 px-4 py-4 shadow-sm sm:px-6 lg:hidden bg-white">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-primary lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex-1 text-sm font-semibold leading-6 text-gray-900">
            <img
              width="134"
              height="23"
              className="max-w-full"
              src={logo}
              alt="VeeMind"
            />
          </div>
        </div>

        <div className="lg:min-h-screen">
          <main className="p-8 lg:pl-72 flex flex-col h-full">
            <div className="h-full flex flex-col max-w-full">
              <Outlet
                context={{
                  token,
                  notify,
                  userDetails,
                  getUserDetails,
                  frontendurl,
                  logOut,
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default SuperAdminLayout;
